import { Meeting } from "@lib/store";
import { LocalDateTimeString, LocalTimeString } from "@lib/store/src/data/timeslots/apiTypes";
import {
  dateKeyForDateTimeString,
  dateToLocalDateTimeString,
  hhMmToMinuteOfDay,
  localDateTimeStringToHHMMss,
  localDateTimeToMinuteOfDay,
} from "@lib/store/src/data/timeslots/dateTimeHelpers";
import { TimeslotsEvents } from "@lib/infrastructure/src/infrastructure/analytics/actions/types/TimeslotsEvents";
import { format } from "date-fns";

export type LocalTimespan = {
  localStart: LocalDateTimeString;
  localEnd: LocalDateTimeString;
};

export const adjustLocalTime = (ts: LocalDateTimeString, hhmm: LocalTimeString): LocalDateTimeString =>
  `${dateKeyForDateTimeString(ts)}T${hhmm}:00`;

export const hhmmEqual = (a: LocalDateTimeString, b: LocalDateTimeString): boolean => {
  return localDateTimeStringToHHMMss(a) === localDateTimeStringToHHMMss(b);
};

export const timespansEqual = (a: LocalTimespan, b: LocalTimespan): boolean => {
  return hhmmEqual(a.localStart, b.localStart) && hhmmEqual(a.localEnd, b.localEnd);
};

/**
 * Suggests a shortened workspace reservation to avoid conflicts with scheduled meetings.
 * 1. Only suggesting adjustments when the user hasn't manually customized their timespan
 * 2. First constraining the reservation to standard office hours (9:30 AM - 5:00 PM)
 * 3. Then adjusting the start time forward to avoid conflicts with meeting starts
 * 4. And adjusting the end time backward to avoid conflicts with meeting ends
 * 5. Only suggesting changes that provide meaningful time savings (≥30 minutes)
 * 6. Ensuring the final suggestion is still practical (≥2 hours duration)
 *
 * @param currentTimespan The timespan as currently selected
 * @param defaultTimespan The timespan as it was initially set (used to define whether the user has customized their input)
 * @param meetingsOnDate The meetings that impact the adjustments
 * @returns a proposed adjustment if we feel it helps the case, null if no beneficial adjustment is possible
 */
export const trySuggestShortenedReservation = (
  currentTimespan: LocalTimespan,
  defaultTimespan: LocalTimespan,
  meetingsOnDate: Array<{ startDateTime: string; endDateTime: string }>,
): LocalTimespan | null => {
  if (!timespansEqual(currentTimespan, defaultTimespan)) return null;

  const range = { ...currentTimespan };

  const officeHoursStart = adjustLocalTime(currentTimespan.localStart, "09:30");
  const officeHoursEnd = adjustLocalTime(currentTimespan.localEnd, "17:00");

  // If the user hasn't modified start and end times, perform meeting based trims on office hours
  if (range.localStart === defaultTimespan.localStart && range.localStart < officeHoursStart) {
    range.localStart = officeHoursStart;
  }

  if (range.localEnd === defaultTimespan.localEnd && range.localEnd > officeHoursEnd) {
    range.localEnd = officeHoursEnd;
  }

  let start = range.localStart;
  let end = range.localEnd;

  let startTouched = false;
  let endTouched = false;

  const meetingRanges = meetingsOnDate.map(m => ({
    start: dateToLocalDateTimeString(new Date(m.startDateTime)),
    end: dateToLocalDateTimeString(new Date(m.endDateTime)),
  }));

  // While start overlaps with a meeting, change it to the end of that meeting
  for (const m of meetingRanges.sort((a, b) => a.start.localeCompare(b.start) || a.end.localeCompare(b.end))) {
    // Skip forward
    if (m.end < start) continue;

    // End if the meeting starts after the start of the range
    if (m.start > start) break;

    // If we get here, the meeting overlaps with our start
    start = m.end;
    startTouched = true;
  }

  // Reverse and do the same on the end
  for (const m of meetingRanges.sort((a, b) => b.end.localeCompare(a.end) || b.start.localeCompare(a.start))) {
    // Skip forward
    if (m.start > end) continue;

    // End if the meeting ends before the end of the range we're done
    if (m.end < end) break;

    // If we get here, the meeting overlaps with our start
    end = m.start;
    endTouched = true;
  }

  if (!startTouched && !endTouched) return null;

  const corrected = {
    localStart: startTouched ? start : currentTimespan.localStart,
    localEnd: endTouched ? end : currentTimespan.localEnd,
  };

  if (timespansEqual(currentTimespan, corrected)) return null;

  const m0 = localDateTimeToMinuteOfDay(corrected.localStart);
  const m1 = localDateTimeToMinuteOfDay(corrected.localEnd);
  const correctedDuration = m1 - m0;

  // Never suggest bookings shorter than 2 hours
  if (correctedDuration < 120) return null;

  // Don't suggest improvements that are not that impactful
  const initialDuration =
    localDateTimeToMinuteOfDay(currentTimespan.localEnd) - localDateTimeToMinuteOfDay(currentTimespan.localStart);
  if (initialDuration - correctedDuration < 30) return null;

  return corrected;
};

export const constructAppInsightsEvent = (
  meetings: Meeting[],
  suggestion: { localStart: LocalDateTimeString; localEnd: LocalDateTimeString },
  initialRange: { localStart: LocalDateTimeString; localEnd: LocalDateTimeString },
  mutation: TimeslotsEvents["Timeslots_WorkspaceMeetingOverlapSuggestionInteraction"]["mutation"],
  action: TimeslotsEvents["Timeslots_WorkspaceMeetingOverlapSuggestionInteraction"]["action"],
): TimeslotsEvents["Timeslots_WorkspaceMeetingOverlapSuggestionInteraction"] => {
  return {
    action,
    mutation,
    initialRange: [
      localDateTimeToMinuteOfDay(initialRange.localStart),
      localDateTimeToMinuteOfDay(initialRange.localEnd),
    ],
    suggestedRange: [
      localDateTimeToMinuteOfDay(suggestion.localStart),
      localDateTimeToMinuteOfDay(suggestion.localEnd),
    ],
    meetingMinutes: meetings.map(m => [
      hhMmToMinuteOfDay(format(new Date(m.startDateTime), "HH:mm")),
      hhMmToMinuteOfDay(format(new Date(m.endDateTime), "HH:mm")),
    ]),
  };
};
